import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/utils/supabase';

export interface Slide {
  id: string | number;
  image: string;
  redirectUrl?: string;
  display_order?: number;
}

interface CarouselState {
  slides: Slide[];
  isLoading: boolean;
  lastFetchedAt: number;
  fetchSlides: (force?: boolean) => Promise<void>;
  initRealtimeSubscription: () => void;
  cleanupRealtimeSubscription: () => void;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const useCarouselStore = create<CarouselState>()(
  persist(
    (set, get) => ({
      slides: [],
      isLoading: false,
      lastFetchedAt: 0,

      fetchSlides: async (force = false) => {
        const now = Date.now();
        const { slides, lastFetchedAt } = get();

        // If we have slides and they are fresh, and we aren't forcing, skip fetch to save DB hits
        if (!force && slides.length > 0 && now - lastFetchedAt < CACHE_TTL) {
          return;
        }

        if (slides.length === 0) set({ isLoading: true });

        try {
          const { data, error } = await supabase
            .from('carousels')
            .select('*')
            .eq('is_active', true)
            .order('display_order', { ascending: true });

          if (error) throw error;

          if (data && data.length > 0) {
            set({
              slides: data.map((d: { id: string | number; image_url?: string; redirect_url?: string; display_order?: number }) => ({
                id: d.id,
                image: d.image_url || '',
                redirectUrl: d.redirect_url || undefined,
                display_order: d.display_order,
              })),
              isLoading: false,
              lastFetchedAt: now,
            });
          } else {
            set({
              slides: [{ id: 1, image: '/icons/logo-3.png', redirectUrl: '/products', display_order: 1 }],
              isLoading: false,
              lastFetchedAt: now,
            });
          }
        } catch (error) {
          console.error('Failed to fetch slides:', error);
          set({ isLoading: false });
        }
      },

      initRealtimeSubscription: () => {
        const channels = supabase.getChannels();
        if (channels.find((c) => c.topic.includes('carousel_updates'))) return;

        supabase
          .channel('carousel_updates')
          .on(
            'postgres_changes',
            { event: '*', schema: 'public', table: 'carousels' },
            (payload) => {
              const { eventType, new: newRecord, old: oldRecord } = payload;
              
              set((state) => {
                 let updatedSlides = [...state.slides];
                 const record = eventType === 'DELETE' ? oldRecord : newRecord;
                 
                 if (eventType === 'INSERT' && record.is_active) {
                    updatedSlides.push({
                        id: record.id,
                        image: record.image_url || '',
                        redirectUrl: record.redirect_url || undefined,
                        display_order: record.display_order || 99
                    });
                 } else if (eventType === 'UPDATE') {
                    if (record.is_active) {
                        const exists = updatedSlides.find(s => s.id === record.id);
                        if (exists) {
                            updatedSlides = updatedSlides.map(s => s.id === record.id ? {
                                id: record.id,
                                image: record.image_url || '',
                                redirectUrl: record.redirect_url || undefined,
                                display_order: record.display_order || 99
                            } : s);
                        } else {
                            updatedSlides.push({
                                id: record.id,
                                image: record.image_url || '',
                                redirectUrl: record.redirect_url || undefined,
                                display_order: record.display_order || 99
                            });
                        }
                    } else {
                        // Deactivated
                        updatedSlides = updatedSlides.filter(s => s.id !== record.id);
                    }
                 } else if (eventType === 'DELETE') {
                    updatedSlides = updatedSlides.filter(s => s.id !== record.id);
                 }
                 
                 // Sort by display order
                 updatedSlides.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
                 
                 if (updatedSlides.length === 0) {
                     updatedSlides = [{ id: 1, image: '/icons/logo-3.png', redirectUrl: '/products', display_order: 1 }];
                 }
                 
                 return { slides: updatedSlides };
              });
            }
          )
          .subscribe();
      },

      cleanupRealtimeSubscription: () => {
        const channels = supabase.getChannels();
        const channel = channels.find((c) => c.topic.includes('carousel_updates'));
        if (channel) supabase.removeChannel(channel);
      },
    }),
    {
      name: 'carousel-cache',
      partialize: (state) => ({ slides: state.slides, lastFetchedAt: state.lastFetchedAt }),
    }
  )
);
